import { ValidationError } from "sequelize"
import {
    SilSafetyFunction,
    SilSafetyFunctionCreate,
    SilSubsystem,
    SilSubsystemBlock,
} from "server/models"
import { NONE, VALUE, UNLIMITED, ASC } from "const"
import { OrderType, deepCopy, SilDeviceFilter } from "client/utils"
import {
    GET, POST, DELETE, sort, search, validate,
} from "client/store/utils"
import {
    PackageSettings,
    UserData,
    LangType,

    SilSafetyFunctionList,
    SilSafetyFunctionValidation,
    SilSafetyFunctionFilters,

    LoadSilProjectDevicesByFilter,
    DeleteSilResultSafetyFunction,
} from "client/store"

export const LoadAllSilSafetyFunctions = (projectId: number) => (
    GET(`/silSafetyFunction/all/projectId/${projectId}`)
    .then((safetyFunctions: SilSafetyFunction[]) => {
        const safetyFunctionList: SilSafetyFunctionList = {}
        safetyFunctions.forEach((safetyFunction) => {
            safetyFunctionList[safetyFunction.id] = safetyFunction
        })
        return safetyFunctionList
    })
)

export const LoadSilSafetyFunctionById = (
    safetyFunctionId: number,
    projectId: number,
) => (
    GET(`/silSafetyFunction/id/${safetyFunctionId}/projectId/${projectId}`)
    .then((safetyFunction: SilSafetyFunction) => {
        // load attached project devices
        const subsystems: SilSubsystem[] = safetyFunction ? deepCopy(safetyFunction.subsystems) : []
        const subsystemPromiseArray: Promise<any>[] = []

        subsystems.forEach((subsystem, subsystemIndex) => {
            const blocks: SilSubsystemBlock[] = deepCopy(subsystem.blocks)
            const blockPromiseArray: Promise<any>[] = []

            blocks.forEach((block, blockIndex) => {
                const filters: SilDeviceFilter = {
                    projectId,
                    deviceId: block.deviceId,
                    projectDeviceId: block.projectDeviceId,
                    qidProductId: block.qidProductId,
                    qidModelId: block.qidModelId,
                    qidSafetyFunctionId: block.qidSafetyFunctionId,
                    qidOptionId: block.qidOptionId,
                    qidDatapointId: block.qidDatapointId,
                    qidMethod: block.qidMethod,
                }

                blockPromiseArray.push(
                    LoadSilProjectDevicesByFilter(filters).then((projectDevices) => {
                        if (projectDevices.length === 1) {
                            blocks[blockIndex] = {
                                ...blocks[blockIndex],
                                projectDevice: projectDevices[0],
                            }
                        }
                    }),
                )
            })

            subsystemPromiseArray.push(
                Promise.all(blockPromiseArray).then(() => {
                    subsystems[subsystemIndex] = {
                        ...subsystems[subsystemIndex],
                        blocks,
                    }
                }),
            )
        })

        return Promise.all(subsystemPromiseArray).then(() => {
            // additional sorting for old subsystems - they have "null" value in "order" column
            if (subsystems.length > 0 && subsystems[0].order === null) {
                subsystems.sort((a, b) => sort(a, b, "id", ASC))
            }

            return {
                ...safetyFunction,
                subsystems,
            } as SilSafetyFunction
        })
    })
)

export const SaveSafetyFunction = (
    id: number,
    safetyFunction: SilSafetyFunctionCreate,
) => (
    POST(
        "/silSafetyFunction/save",
        JSON.stringify({ id, safetyFunction }),
    )
)

export const SaveSilSafetyFunction = (
    id: number,
    safetyFunction: SilSafetyFunctionCreate,
    maxNumber: number,
    lang: LangType,
) => (
    LoadAllSilSafetyFunctions(safetyFunction.projectId).then((res1) => {
        const safetyFunctionsNumber = Object.keys(res1).length
        if (!id && maxNumber !== null && safetyFunctionsNumber >= maxNumber) {
            return {
                newSafetyFunctionId: null,
                validation: <SilSafetyFunctionValidation>{
                    name: lang.maxSafetyFunctionsReached.replace("{0}", maxNumber.toString()),
                },
            }
        }

        return SaveSafetyFunction(id, safetyFunction).then((res) => {
            const newSafetyFunction = res as SilSafetyFunction
            const error = res as ValidationError
            return {
                newSafetyFunctionId: newSafetyFunction.id || null,
                validation: <SilSafetyFunctionValidation>validate(error, lang),
            }
        })
    })
)

export const DeleteSilSafetyFunction = (
    safetyFunctionId: number,
    projectId: number,
) => (
    // delete origin and calculated safety function
    DELETE(`/silSafetyFunction/delete/${safetyFunctionId}/${projectId}`)
    .then((removedRows: number) => (
        DeleteSilResultSafetyFunction(safetyFunctionId, projectId)
        .then(() => removedRows)
    ))
)

export const SortSilSafetyFunctions = (
    safetyFunctions: SilSafetyFunction[],
    sortColumn: keyof SilSafetyFunction,
    sortOrder: OrderType,
    filters: SilSafetyFunctionFilters,
): number[] => {
    const safetyFunctionsFiltered = safetyFunctions.filter((safetyFunction) => {
        let showRow = true
        if (filters.name && safetyFunction.name.toUpperCase().indexOf(filters.name.toUpperCase()) < 0) showRow = false
        else if (
            filters.calculationMethod
            && safetyFunction.calculationMethodDangerous !== filters.calculationMethod
            && safetyFunction.calculationMethodSafe !== filters.calculationMethod
        ) showRow = false
        else if (filters.mode && safetyFunction.mode !== filters.mode) showRow = false

        else if (
            (filters.calculated === false || filters.calculated === true)
            && safetyFunction.calculated !== filters.calculated
        ) showRow = false

        else if (filters.search) showRow = search(filters.search, safetyFunction)
        return showRow
    })

    const safetyFunctionIds: number[] = []
    safetyFunctionsFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))
    safetyFunctionsFiltered.forEach((safetyFunction) => safetyFunctionIds.push(safetyFunction.id))

    return safetyFunctionIds
}


export const GetMaxSilSafetyFunctions = (
    packageSettings: PackageSettings,
    userData: UserData,
    safetyFunctionsNumber?: number,
    lang?: LangType,
) => {
    const { silSafetyFunctions, silSafetyFunctionsValue } = packageSettings

    let maxSafetyFunctions = 0
    if (silSafetyFunctions === NONE) maxSafetyFunctions = 0
    if (silSafetyFunctions === VALUE && silSafetyFunctionsValue) maxSafetyFunctions = silSafetyFunctionsValue
    if (silSafetyFunctions === UNLIMITED) maxSafetyFunctions = null
    if (userData.accountId === 1) maxSafetyFunctions = null

    // feedbacks
    let feedback: string = ""
    if (lang) {
        if (maxSafetyFunctions === null) {
            feedback = lang.maxSafetyFunctionsUnlimited
        } else {
            if (safetyFunctionsNumber < maxSafetyFunctions) feedback = lang.maxSafetyFunctions
            if (safetyFunctionsNumber === maxSafetyFunctions) feedback = lang.maxSafetyFunctionsReached
            if (safetyFunctionsNumber > maxSafetyFunctions) feedback = lang.maxSafetyFunctionsExceeded
            feedback = feedback.replace("{0}", maxSafetyFunctions.toString())
        }
    }

    return {
        maxSafetyFunctions,
        safetyFunctionsFeedback: feedback,
    }
}
